<template>
  <BaseSelectDropdown
    ref="dropdown"
    v-tooltip="hintConfig"
    :active="dropdownActive"
    :label="label"
    :label-position="labelPosition"
    :disabled="disabled"
    :large="large"
    :small="small"
    :allow-width-overflow="allowWidthOverflow"
    :popper-class="popperClass"
    :is-in-modal="isInModal"
    :dropdown-width="forceDropdownWidth"
    class="m-multiselect"
    @trigger="handleDropdownTrigger"
    @open="onOpen"
    @close="onClose"
    @handle-click-navigation="handleClickNavigation"
  >
    <template slot="header">
      <div
        v-overflow-title="valueText"
        class="m-multiselect__content"
        :class="{
          'm-multiselect__content_empty': !checkedCount,
          'm-multiselect__content_small': small
        }"
      >
        <template v-if="allChecked">
          {{ selectAllText }}
        </template>
        <template v-else-if="checkedCount">
          {{ valueText }}
        </template>
        <template v-else>
          {{ placeholder }}
        </template>
      </div>
    </template>
    <template slot="dropdown">
      <template v-if="isLoading">
        <MSelectControlSkeletonLoader />
      </template>
      <template v-else>
        <MSearchInline
          v-if="allowSearch"
          ref="searchInput"
          v-model="searchQuery"
          class="m-multiselect__search"
          :placeholder="searchPlaceholder"
          :emit-input-on-change="false"
          @handle-click-navigation="handleClickNavigation"
        />
        <MCheckbox
          v-if="selectAllEnabled"
          v-show="matchesFound"
          class="m-multiselect__choice"
          :value="allChecked"
          :intermediate="isIntermediate"
          :label-text="selectAllText"
          :is-focus="currentFocusItemId === -1"
          @click.native="checkAll(!allChecked)"
          @handle-click-navigation="handleClickNavigation"
        />
        <template v-if="groupedChoices.length">
          <template
            v-for="group in groupedChoices"
          >
            <div
              v-if="group.choices && group.choices.length > 0"
              :key="`sticky-trigger-${group[groupIdAttr]}`"
              :class="`m-multiselect__sticky-trigger-${group[groupIdAttr]}`"
            />
            <div
              v-show="groupIsFiltered(group)"
              :key="group[groupIdAttr]"
              class="m-multiselect__group"
              :class="{'m-multiselect__group_sticky': sticky[group[groupIdAttr]]}"
            >
              <MProviderIcon
                v-if="group[groupIconAttr]"
                :icon="group[groupIconAttr]"
                class="m-multiselect__group-icon"
              />
              <span
                v-overflow-title="group[groupTextAttr]"
                class="m-multiselect__group-text"
              >{{ group[groupTextAttr] }}</span>
            </div>
            <MMultiSelectChoices
              :key="`${group[groupIdAttr]}-choices`"
              :is-visible="dropdownActive"
              :choices="group.choices"
              :lazy-render="lazyRender"
              :filtered-choices-ids="filteredChoicesIds"
              :value="innerValue"
              :id-attr="idAttr"
              :disable-option-predicate="disableOptionPredicate"
              :disable-add-items="disableAddItems"
              :text-attr="textAttr"
              :show-id="showId"
              :display-id-attr="displayIdAttr"
              :format-fn="formatFn"
              :current-focus-item-id="currentFocusItemId"
              @toggleItem="handleItemClick"
              @handle-click-navigation="handleClickNavigation"
            />
          </template>
        </template>
        <template v-else-if="filteredChoices.length">
          <MMultiSelectChoices
            :is-visible="dropdownActive"
            :choices="filteredChoices"
            :lazy-render="lazyRender"
            :value="innerValue"
            :id-attr="idAttr"
            :disable-option-predicate="disableOptionPredicate"
            :disable-add-items="disableAddItems"
            :text-attr="textAttr"
            :show-id="showId"
            :display-id-attr="displayIdAttr"
            :format-fn="formatFn"
            :current-focus-item-id="currentFocusItemId"
            @toggleItem="handleItemClick"
            @handle-click-navigation="handleClickNavigation"
          />
        </template>
        <div
          v-show="!matchesFound"
          class="m-multiselect__no-items-message"
        >
          {{ noItemsMessage }}
        </div>
        <div
          class="m-multiselect__actions-container"
          :class="{'_reverse': reverseActionBtn}"
        >
          <MButton
            link
            gray
            class="m-multiselect__actions-button"
            @click="resetSelected"
          >
            {{ resetButtonText }}
          </MButton>
          <MButton
            link
            class="m-multiselect__actions-button"
            @click="applySelected"
          >
            OK
          </MButton>
        </div>
      </template>
    </template>
  </BaseSelectDropdown>
</template>

<script>
  import _ from 'lodash';
  _.noConflict();

  import 'utils/string/format';

  import getTr from 'translation';

  import MButton from 'components/Inputs/Basic/MButton';
  import MProviderIcon from 'components/Design/MProviderIcon';
  import MCheckbox from 'components/Inputs/Basic/MCheckbox';
  import MSearchInline from 'components/common/MSearchInline';
  import BaseSelectDropdown from 'components/Inputs/Select/common/BaseSelectDropdown/BaseSelectDropdown';
  import MSelectControlSkeletonLoader from 'components/Inputs/Select/MSelectControl/MSelectControlSkeletonLoader';

  import {MVue} from 'index';
  import filterSearch from 'utils/search/filterSearch';
  import MMultiSelectChoices from 'components/Inputs/Select/MMultiSelect/MMultiSelectChoices';

  const defaultSearchFn = (item, searchQuery, component) => {
    const textMatch = item[component.textAttr].toLowerCase().
      includes(searchQuery.toLowerCase());
    const idMatch = String(item[component.displayIdAttr]).
      toLowerCase().
      includes(searchQuery.toLowerCase());
    return component.showId ? textMatch || idMatch : textMatch;
  };

  const defaultGroupSearchFn = (item, searchQuery, component) => {
    const textMatch = item[component.groupTextAttr].toLowerCase().
      includes(searchQuery.toLowerCase());
    return textMatch;
  };

  export default {
    name: 'MMultiSelect',
    components: {
      BaseSelectDropdown,
      MSearchInline,
      MButton,
      MProviderIcon,
      MCheckbox,
      MSelectControlSkeletonLoader,
      MMultiSelectChoices
    },
    props: {

      // ids of selected items
      value: {
        type: Array,
        default: Array
      },
      choices: {
        type: Array,
        default: Array
      },
      disabled: {
        type: Boolean,
        default: false
      },
      textAttr: {
        type: String,
        default: 'text'
      },
      idAttr: {
        type: String,
        default: 'id'
      },
      searchFn: {
        type: Function,
        default: defaultSearchFn
      },
      groupSearchFn: {
        type: Function,
        default: defaultGroupSearchFn
      },
      groupAttr: {
        type: String,
        default: 'group'
      },
      groupIdAttr: {
        type: String,
        default: 'id'
      },
      groupTextAttr: {
        type: String,
        default: 'text'
      },
      groupIconAttr: {
        type: String,
        default: 'icon'
      },
      groups: {
        type: Array,
        default: () => []
      },
      required: {
        type: Boolean,
        default: false
      }, // defines that field must be filled
      placeholder: {
        type: String,
        default: ''
      },
      searchPlaceholder: {
        type: String,
        default: () => getTr('search')
      }, // placeholder that will be shown in search input
      allowSearch: {
        type: Boolean,
        default: true
      },
      allowSelectAll: {
        type: Boolean,
        default: true
      },
      allowEmptyAll: {
        type: Boolean,
        default: true
      },
      noItemsMessage: {
        type: String,
        default: () => getTr('no_matches_found')
      },
      label: {
        type: String,
        default: ''
      },
      labelPosition: {
        type: String,
        default: 'top',
        choices: ['left', 'top']
      },
      large: {
        type: Boolean,
        default: false
      },
      small: {
        type: Boolean,
        default: false
      },
      showId: {
        type: Boolean,
        default: true
      },
      showIdAttr: {
        type: String,
        default: undefined
      },
      popperClass: {
        type: BaseSelectDropdown.props.popperClass.type,
        default: BaseSelectDropdown.props.popperClass.default
      },
      allowWidthOverflow: Boolean,
      maxItemsToSelect: {
        type: Number,
        default: 0
      },
      maxItemsHintText: {
        type: String,
        default: ''
      },
      disableOptionPredicate: {
        type: Function,
        default: () => false
      },
      resetButtonText: {
        type: String,
        default: () => getTr('reset')
      },
      closeOnReset: {
        type: Boolean,
        default: true
      },
      clearOnReset: {
        type: Boolean,
        default: true
      },
      disabledValuesHint: {
        type: String,
        default: ''
      },
      emptyValuesHint: {
        type: String,
        default: ''
      },
      isInModal: {
        type: Boolean,
        default: false
      },
      isLoading: {
        type: Boolean,
        default: false
      },
      formatFn: {
        type: Function,
        default: undefined
      },
      lazyRender: {
        type: Boolean,
        default: false
      },
      forceDropdownWidth: {
        type: Number,
        default: null
      },
      selectAllText: {
        type: String,
        default: () => getTr('select_all')
      },
      reverseActionBtn: {
        type: Boolean,
        default: false
      },
      itemsChosenText: {
        type: String,
        default: () => getTr('items_chosen_format')
      }
    },
    data () {
      return {
        hintText: '',
        orderedChoices: [],
        searchQuery: '',
        dropdownActive: false,
        sticky: {},
        observers: [],
        innerValue: [],
        currentFocusItemIndex: null
      };
    },
    computed: {
      isIntermediate () {
        return !this.allChecked && this.innerValue.length !== 0;
      },
      currentFocusItemId () {
        return this.itemsIdAvailableFocus[this.currentFocusItemIndex] || null;
      },
      itemsAvailableFocus () {
        let resultItems = [];

        if (this.selectAllEnabled) {
          resultItems.push({id: -1});
        }

        try {
          if (this.groupedChoices.length) {
            const onlyChoicesIdList = this.groupedChoices.
              map(group => group.choices.filter(choice => !choice.disabled));
            resultItems = resultItems.concat(onlyChoicesIdList.flat() || []);
          } else if (this.filteredChoices.length) {
            resultItems = resultItems.concat(this.filteredChoices.filter(item => !item.disabled) || []);
          }
        } catch (e) {
          console.warn('MMultiSelect: Error occurred while parsing the choices data for focus logic', e);
        }

        return resultItems;
      },
      itemsIdAvailableFocus () {
        return this.itemsAvailableFocus.map(item => item[this.idAttr] || item.id);
      },
      filteredSearchQuery () {
        return filterSearch(this.searchQuery);
      },
      hintConfig () {
        if (this.hintText) {
          return {
            content: this.hintText,
            show: true,
            placement: 'top',
            trigger: 'manual',
            hideOnTargetClick: true
          };
        }
        return '';
      },
      disableAddItems () {
        return this.maxItemsToSelect > 0 && this.valueItems.length >= this.maxItemsToSelect;
      },
      matchesFound () {
        return this.filteredGroupdsIdsSet.size || this.filteredChoicesSet.size;
      },
      selectAllEnabled () {
        if (this.maxItemsToSelect > 0) {
          return this.allowSelectAll && this.orderedChoices.length <= this.maxItemsToSelect;
        }
        return this.allowSelectAll;
      },
      valueItems () {
        return this.innerValue.map(itemId => this.getChoiceById(itemId)).filter(item => Boolean(item));
      },
      valueSet () {
        return new Set(this.innerValue);
      },
      valueText () {
        if (this.checkedCount !== 1) {
          return this.itemsChosenText.format(this.checkedCount);
        }
        const item = this.valueItems[0];
        return this.itemText(item);
      },
      groupedChoices () {
        if (this.groups.length < 1) {
          return [];
        }

        const groupsMap = new Map();
        for (const group of this.groups) {
          groupsMap.set(group[this.groupIdAttr], {...group});
          groupsMap.get(group[this.groupIdAttr]).choices = [];
        }

        for (const choice of this.orderedChoices) {
          const groupInMap = groupsMap.get(choice[this.groupAttr]);
          if (!this.filterChoice(choice)) {
            continue;
          }
          if (groupInMap) {
            groupInMap.choices.push(choice);
          } else {
            console.warn('Failed to find group with id {0}'.format(choice[this.groupAttr]));
          }
        }

        const res = [];
        groupsMap.forEach(group => {
          if (group.choices.length) {
            res.push(group);
          }
        });
        return res;
      },
      filteredGroupdsIdsSet () {
        return new Set(this.groupedChoices.filter(
          (group) => group.choices.some(item => this.filteredChoicesSet.has(item[this.idAttr]))
        ).map(g => g[this.groupIdAttr]));
      },
      filteredChoices () {
        return this.orderedChoices.filter(item => this.filterChoice(item));
      },
      filteredChoicesIds () {
        let items = this.orderedChoices.filter(item => !item.disabled);
        if (this.filteredSearchQuery.length > 0) {
          items = this.orderedChoices.filter(item => this.filterChoice(item));
        }
        return items.map(item => item[this.idAttr]);
      },
      filteredChoicesSet () {
        return new Set(this.filteredChoicesIds);
      },
      matchedGroupsIds () {
        let items = this.groups;
        if (this.filteredSearchQuery.length > 0) {
          items = this.groups.filter(item => this.groupSearchFn(item, this.filteredSearchQuery, this));
        }
        return items.map(item => item[this.idAttr]);
      },
      allChecked () {
        return Boolean(this.filteredChoicesIds.length) &&
          this.filteredChoicesIds.reduce((acc, item) => acc && this.valueSet.has(item), true);
      },
      checkedCount () {
        return this.valueItems.length;
      },
      displayIdAttr () {
        return this.showIdAttr || this.idAttr;
      }
    },
    watch: {
      itemsIdAvailableFocus: {
        handler () {
          this.currentFocusItemIndex = null;
        },
        deep: true
      },
      choices: {
        handler () {
          this.orderChoices();

          // filter selected choices based on changed choices
          const active = this.choices.filter(item => this.innerValue.includes(item[this.idAttr])).
            map(i => i[this.idAttr]);
          if (active.length !== this.valueItems.length) {
            this.innerValue = [...active];
          }
        },
        immediate: true,
        deep: true
      },
      currentFocusItemIndex (newValue) {
        if (this.allowSearch && newValue !== null) {
          this.unFocusSearchInput();
        }
      },
      groupedChoices () {
        this.disconnectObservers();
        this.connectObservers();
      },
      value: {
        immediate: true,
        handler (val) {
          if (val) {
            this.innerValue = [...val];
          }
        }
      },
      isLoading: {
        handler (val) {
          if (!val) {
            this.orderChoices();
          }
        }
      }
    },
    beforeDestroy () {
      this.disconnectObservers();
    },
    methods: {
      getIndexFocusItem (id) {
        let resultIndex = null;

        for (let index = 0; index < this.itemsIdAvailableFocus.length; index++) {
          if (this.itemsIdAvailableFocus[index] === id) {
            resultIndex = index;
            break;
          }
        }

        return resultIndex;
      },
      onFocusCurrentComponent () {
        this.$refs.dropdown?.$el.focus();
      },
      unFocusSearchInput () {
        if (!this.allowSearch) {
          return;
        }

        const searchInputComponent = this.$refs?.searchInput?.$refs?.input;
        if (searchInputComponent) {
          searchInputComponent.blur();
          this.onFocusCurrentComponent();
        } else {
          this.$nextTick(this.unFocusSearchInput);
        }
      },
      handleClickNavigation (e) {
        e.stopPropagation();

        if (['ArrowUp', 'ArrowDown', 'Enter'].includes(e.key) && this.dropdownActive === false) {
          this.handleDropdownTrigger(true);
          return;
        }

        switch (e.key) {
        case 'ArrowUp':
          if (!this.currentFocusItemIndex) {
            break;
          }

          this.currentFocusItemIndex = this.currentFocusItemIndex - 1;
          break;
        case 'ArrowDown':
          if (this.currentFocusItemIndex === null && this.itemsIdAvailableFocus.length > 0) {
            this.currentFocusItemIndex = 0;
            break;
          } else if (this.itemsIdAvailableFocus.length - 1 === this.currentFocusItemIndex) {
            break;
          }

          this.currentFocusItemIndex = this.currentFocusItemIndex + 1;
          break;
        case 'Enter':
          if (!this.currentFocusItemId && this.currentFocusItemId !== 0) {
            break;
          }

          if (['number', 'string'].includes(typeof this.currentFocusItemId)) {
            this.activateChoiceWithId(this.currentFocusItemId);
          }

          break;
        case 'Escape':
          this.handleDropdownTrigger(false);
          this.applySelected();
          break;
        default:
          break;
        }
      },
      itemIsActive (item) {
        return this.valueSet.has(item[this.idAttr]);
      },
      itemIsDisabled (item) {
        if (this.disableAddItems) {
          this.disableOptionPredicate('', this.innerValue);
          return !this.itemIsActive(item) && this.disableAddItems;
        }
        return this.disableOptionPredicate(item, this.innerValue, this.choices);
      },
      handleItemClick (item) {
        if (this.itemIsDisabled(item) && !this.itemIsActive(item) && this.disableAddItems) {
          this.showHint(this.maxItemsHintText);
        }
        if (!this.disableAddItems && this.itemIsDisabled(item) && !this.itemIsActive(item)) {
          this.showHint(this.disabledValuesHint);
        }
        if (this.itemIsDisabled(item)) {
          return;
        }

        this.currentFocusItemIndex = this.getIndexFocusItem(item[this.idAttr]);

        this.itemChecked(item, !this.valueSet.has(item[this.idAttr]));
      },
      orderChoices () {
        this.orderedChoices = this.choices.slice().sort(
          (a, b) => this.valueSet.has(b[this.idAttr]) - this.valueSet.has(a[this.idAttr])
        );
      },
      disconnectObservers () {
        this.observers.forEach(observer => observer.disconnect());
      },
      connectObservers () {
        if ('IntersectionObserver' in window && this.$refs.dropdown.$refs.dropdown) {
          const root = this.$refs.dropdown.$refs.dropdown.$el;
          this.observers = this.groupedChoices.map(group => {
            if (group.choices.length > 0) {
              const groupId = group[this.groupIdAttr];
              const observer = new IntersectionObserver(this.triggerSticky.bind(this, groupId), {root});
              const trigger = root.querySelector(`.m-multiselect__sticky-trigger-${groupId}`);
              if (trigger) {
                observer.observe(trigger);
              }
              return observer;
            }
          }).filter(observer => Boolean(observer));
        }
      },
      triggerSticky (groupId, entites) {
        MVue.set(this.sticky, groupId, !entites[0].isIntersecting);
      },
      filterChoice (item) {
        if (this.groups.length > 0) {

          // if item is from matched group, we do not filter item
          if (this.matchedGroupsIds.includes(item[this.groupAttr])) {
            return true;
          }
        }
        return this.searchFn(item, this.filteredSearchQuery, this);
      },
      getChoiceById (itemId) {
        return this.choices.filter(item => {

          // id can be string or numeric
          if (_.isFinite(item[this.idAttr])) {
            return Number(item[this.idAttr]) === Number(itemId);
          }
          return item[this.idAttr] === itemId;

        })[0];
      },
      choiceIsFiltered (item) {
        return this.filteredChoicesSet.has(item[this.idAttr]);
      },
      groupIsFiltered (group) {
        return this.filteredGroupdsIdsSet.has(group[this.groupIdAttr]);
      },
      itemIcon (item) {
        if (this.groupedChoices.length > 0 && this.groupIconAttr) {
          const group = this.groups.filter(g => g[this.groupIdAttr] === item[this.groupAttr])[0];
          return group && group[this.groupIconAttr];
        }
        return null;
      },
      itemText (item) {
        // in case if choices not fetched yet
        if (!item) {
          return '';
        }

        if (this.formatFn) {
          return this.formatFn(item);
        }

        if (this.showId) {
          return `№${item[this.displayIdAttr]} ${item[this.textAttr]}`;
        }
        return item[this.textAttr];

      },
      onClose () {
        this.$emit('close');
        this.disconnectObservers();
        this.resetState();
      },
      onOpen () {
        this.$emit('open');
        if (!this.isLoading) {
          if (this.allowSearch) {
            this.$nextTick(() => this.$refs.searchInput.$refs.input.focus());
          }
          this._$nextFrame(() => this.connectObservers());
        }
      },
      handleDropdownTrigger (state) {
        if (state) {
          if (['number', 'string'].includes(typeof this.currentFocusItemId)) {
            this.$nextTick(this.unFocusSearchInput);
          }
        } else {
          this.onFocusCurrentComponent();
        }

        this.dropdownActive = state;
      },
      resetState () {
        this.searchQuery = '';
        this.innerValue = [...this.value];
      },
      activateChoiceWithId (itemId) {
        if (itemId === -1 && this.selectAllEnabled) {
          this.checkAll(!this.allChecked);
          return;
        }

        const item = this.itemsAvailableFocus.find(item => (item[this.idAttr] || item.id) === itemId) || null;
        this.handleItemClick(item);
      },
      itemChecked (item, checked) {
        if (this.disabled) {
          return;
        }

        let newValue;
        if (this.valueSet.has(item[this.idAttr]) && !checked) {

          // remove item
          newValue = this.valueItems.filter(current => current[this.idAttr] !== item[this.idAttr]);
        } else if (!this.valueSet.has(item[this.idAttr]) && checked) {
          // add item
          newValue = [...this.valueItems];
          newValue.push(item);
          if (this.maxItemsToSelect > 0 && newValue.length > this.maxItemsToSelect) {
            this.showHint(this.maxItemsHintText);
            return;
          }
        } else {

          // nothing changed
          newValue = this.valueItems;
        }
        this.innerValue = [...newValue.map(val => val[this.idAttr])];
      },
      checkAll (value) {
        if (this.disabled) {
          return;
        }

        this.currentFocusItemIndex = 0;

        let filteredItemsIds = this.filteredChoicesIds;
        if (this.maxItemsToSelect > 0 && filteredItemsIds.length > this.maxItemsToSelect) {
          filteredItemsIds = filteredItemsIds.slice(0, this.maxItemsToSelect);
          this.showHint(this.maxItemsHintText);
        }
        let newValue;
        if (value) {

          // check all filteredChoices
          newValue = [...new Set(this.innerValue.concat(filteredItemsIds))];
        } else {

          // uncheck all filteredChoices
          const selectedItemsIdsSet = new Set(filteredItemsIds);
          newValue = [];
          for (const itemId of this.innerValue) {
            if (!selectedItemsIdsSet.has(itemId)) {
              newValue.push(itemId);
            }
          }
        }
        this.innerValue = [...newValue];
      },
      showHint (text) {
        this.hintText = text;
        setTimeout(() => {
          this.hintText = '';
        }, 5000);
      },
      focus () {
        this.dropdownActive = true;
      },
      blur () {
        this.dropdownActive = false;
      },
      applySelected () {
        if (this.innerValue.length === 0 && !this.allowEmptyAll) {
          if (this.emptyValuesHint) {
            this.showHint(this.emptyValuesHint);
          }
          return;
        }
        this.$emit('input', this.innerValue);
        this.blur();
        this.$emit('close');
      },
      resetSelected () {
        if (this.clearOnReset) {
          // clears inner value
          this.innerValue = [];
          if (!this.allowEmptyAll) {
            return;
          }
          this.$emit('input', this.innerValue);
        } else {
          // resets the previous state
          this.onClose();
        }

        if (this.closeOnReset) {
          this.blur();
          this.$emit('close');
        }
      }
    }
  };
</script>

<style>
  .m-multiselect {
    --options-text-color: var(--secondary-text-color);
    --active-text-color: var(--secondary-text-color);
  }

  .m-multiselect__content {
    font-size: var(--main-font-size);

    color: var(--active-text-color);
    text-overflow: ellipsis;

    white-space: nowrap;

    overflow: hidden;
  }

  .m-multiselect__content_small {
    font-size: 12px;
  }

  .m-multiselect__content_empty {
    color: var(--gray-text-color);
  }

  .m-multiselect__selected-divider {
    padding: 0 5px;

    margin: 5px 0;

    border-bottom: solid 1px var(--border-color);
  }

  .m-multiselect__group-icon {
    flex-shrink: 0;

    width: 12px;
    height: 12px;

    margin: 8px 4px 8px 0;
  }

  .m-multiselect__group {
    font-weight: bolder; /* stylelint-disable-line sh-waqar/declaration-use-variable */

    position: sticky;
    display: flex;
    top: 0;

    flex-shrink: 0;
    align-items: flex-start;

    min-height: 28px;

    padding-left: 8px;
    padding-right: 24px;

    background-color: var(--background-color);
  }

  .m-multiselect__group_sticky {
    box-shadow: 0 1px 4px rgba(129, 129, 129, 0.25);
  }

  .m-multiselect__group_sticky ~ .m-multiselect__group_sticky {
    box-shadow: none;
  }

  .m-multiselect__group-text {
    line-height: 16px;
    text-overflow: ellipsis;

    white-space: unset;

    overflow: hidden;

    /* webkit-box not working in firefox, but in this case it is ok, it will not break layout */
    display: -webkit-box; /* stylelint-disable-line value-no-vendor-prefix */

    padding-top: 6px;
    padding-bottom: 6px;
    /* stylelint-disable property-no-vendor-prefix */
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    /* stylelint-enable property-no-vendor-prefix */
  }

  .m-multiselect__choice {
    flex-grow: 1;
    align-items: baseline;

    min-height: 28px;

    padding-left: 8px;

    &.select-option_focus,
    &:hover {
      background-color: var(--action-background-color);
    }
  }

  .m-multiselect__search {
    position: sticky;
    top: 0;

    box-sizing: border-box;
    height: 28px;

    padding-top: 5px;
    padding-left: 8px;
    padding-right: 8px;

    margin-top: 9px;
    margin-bottom: 8px;
  }

  .m-multiselect__choice .m-checkbox__label {
    font-size: 14px;

    color: var(--options-text-color);
    line-height: 16px;

    white-space: unset;

    /* webkit-box not working in firefox, but in this case it is ok, it will not break layout */
    display: -webkit-box; /* stylelint-disable-line value-no-vendor-prefix */

    flex-grow: 1;

    margin: 6px 24px 6px 8px;
    /* stylelint-disable property-no-vendor-prefix */
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    /* stylelint-enable property-no-vendor-prefix */
  }

  .m-multiselect__choice.m-checkbox .m-checkbox__check-mark {
    margin: 0;
  }

  .m-multiselect__no-items-message {
    font-size: 12px;

    color: var(--gray-text-color);

    margin: 8px 8px 4px;
  }

  .m-multiselect__actions-container {
    position: sticky;
    display: flex;
    bottom: 0;

    justify-content: flex-end;

    padding: 8px 18px 4px 8px;

    background-color: var(--background-color);
    gap: 0 18px;

    &._reverse {
      flex-direction: row-reverse;

      margin-left: 8px;
      gap: 0 16px;
    }
  }

  .m-multiselect__actions-button {
    font-size: 16px;
    font-weight: 500;
  }

  .m-multiselect__skeleton-dropdown {
    overflow-y: auto;
    margin-bottom: 16px;
  }

  .m-multiselect__skeleton-dropdown-node {
    padding: 16px 20px 0;
  }
</style>
